import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Header from "./Header";
import Field from "../../Settings/Content/Components/Field";
import Tooltip from "../../../Components/common/Tooltip/Tooltip";
import TrashBucketIcon from "../../../Components/Icon-SVG/TrashBucketIcon";

import {
  RequestGetMailTransferSettings,
  RequestSetMailTransferSettings,
} from "../../../store/actions/mailGatewayActions";

const MailTransferForm = ({ selectedProduct, selectedHost }) => {
  const dispatch = useDispatch();

  const currentSettings = useSelector((state) => state.mailGateway.mailTransferSettings);

  const [settings, setSettings] = useState({
    passthrough: "",
    bcc_mode: "",
    recvRetries: "",
    sendRetries: "",
    mtaPort: "",
    mtaSize: "",
    allowedClientIps: [],
    rcptDomains: [],
    newClientIp: "",
    newDomain: { domain: "", relay: "" },
  });

  useEffect(() => {
    if (selectedHost) {
      dispatch(RequestGetMailTransferSettings({ hostname: selectedHost }));
    }
  }, [selectedHost, dispatch]);

  useEffect(() => {
    if (currentSettings?.passthrough && selectedHost) {
      setSettings((prevSettings) => ({
        ...prevSettings,
        ...currentSettings,
        bcc_mode: String(currentSettings.bcc_mode ?? "0"),
      }));
    }
  }, [currentSettings, selectedHost]);

  const handleSave = () => {
    if (selectedHost) {
      try {
        dispatch(RequestSetMailTransferSettings({ hostname: selectedHost, settings }));
      } catch (error) {
        console.error("Failed to save settings", error);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSettings((prevSettings) => ({
      ...prevSettings,
      [name]: value,
    }));
  };

  const handleAddClientIp = () => {
    if (settings.newClientIp.trim() !== "") {
      setSettings((prevSettings) => ({
        ...prevSettings,
        allowedClientIps: [...prevSettings.allowedClientIps, settings.newClientIp.trim()],
        newClientIp: "",
      }));
    }
  };

  const handleRemoveClientIp = (index) => {
    setSettings((prevSettings) => ({
      ...prevSettings,
      allowedClientIps: prevSettings.allowedClientIps.filter((_, i) => i !== index),
    }));
  };

  const handleAddDomain = () => {
    if (settings.newDomain.domain.trim() !== "" && settings.newDomain.relay.trim() !== "") {
      setSettings((prevSettings) => ({
        ...prevSettings,
        rcptDomains: [...prevSettings.rcptDomains, settings.newDomain],
        newDomain: { domain: "", relay: "" },
      }));
    }
  };

  const handleRemoveDomain = (index) => {
    setSettings((prevSettings) => ({
      ...prevSettings,
      rcptDomains: prevSettings.rcptDomains.filter((_, i) => i !== index),
    }));
  };

  return (
    <>
      <Header selectedProduct={selectedProduct} selectedHost={selectedHost} />
      <form className="products-body-form">
        <div className="products-body-form__line">
          <div className="mailing__content__security m-b-20">
            <p className="light m-b-10 mailing__content__security__header">
              Passthrough
              <Tooltip
                text={
                  "If enabled, your emails will not be blocked before, during and after processing."
                }
                showFlowGrow={true}
              />
            </p>
            <div className="mailing__content__security__labels">
              <label className="align-items">
                <input
                  className="mailing__content__security__input"
                  type="radio"
                  value="1"
                  name="passthrough"
                  checked={settings.passthrough === "1"}
                  onChange={handleInputChange}
                />
                <p className="margin-left-12">Enabled</p>
              </label>
              <label className="align-items">
                <input
                  className="mailing__content__security__input"
                  type="radio"
                  value="0"
                  name="passthrough"
                  checked={settings.passthrough === "0"}
                  onChange={handleInputChange}
                />
                <p className="margin-left-12">Disabled</p>
              </label>
            </div>
          </div>
        </div>
        <div className="products-body-form__line">
          <Field
            label="Receive Retries"
            width="220px"
            value={settings.recvRetries}
            onChange={(e) =>
              handleInputChange({ target: { name: "recvRetries", value: e.target.value } })
            }
            parentClassName="p-r-20"
            placeholder="Enter retries"
            name="recvRetries"
          />
          <Field
            label="Send Retries"
            width="220px"
            value={settings.sendRetries}
            onChange={(e) =>
              handleInputChange({ target: { name: "sendRetries", value: e.target.value } })
            }
            parentClassName="p-r-20"
            placeholder="Enter retries"
            name="sendRetries"
          />
        </div>
        <div className="products-body-form__line">
          <Field
            label="MTA Port"
            width="220px"
            value={settings.mtaPort}
            onChange={(e) =>
              handleInputChange({ target: { name: "mtaPort", value: e.target.value } })
            }
            parentClassName="p-r-20"
            placeholder="Enter port (e.g., 2525)"
            name="mtaPort"
          />
          <Field
            label="Max Message Size (bytes)"
            width="220px"
            value={settings.mtaSize}
            onChange={(e) =>
              handleInputChange({ target: { name: "mtaSize", value: e.target.value } })
            }
            parentClassName="p-r-20"
            placeholder="Enter size in bytes (e.g., 26214400)"
            name="mtaSize"
          />
          {settings.mtaSize && (
            <p style={{ fontSize: "13px", color: "#888", marginTop: "4px" }}>
              ≈ {(parseInt(settings.mtaSize) / 1024 / 1024).toFixed(1)} MB
            </p>
          )}
        </div>
        <div className="products-body-form__line products-body-form__line--space">
          <Field
            label="Allowed Client IPs"
            tooltipText={"Allowed IP addresses to send email"}
            width="220px"
            value={settings.newClientIp}
            onChange={(e) => setSettings((prev) => ({ ...prev, newClientIp: e.target.value }))}
            parentClassName="p-r-20"
            placeholder="Enter IP (e.g., 192.168.1.1/32)"
            name="newClientIp"
            showFlowGrow={true}
          />
          <button type="button" className="products-body-form__button" onClick={handleAddClientIp}>
            +
          </button>
        </div>
        <ul className="allowed-client-ips-list" style={{ gridColumn: "span 2" }}>
          {settings.allowedClientIps?.length > 0 ? (
            settings.allowedClientIps?.map((ip, index) => (
              <li key={index} className="allowed-client-ip-item">
                {ip}
                <button
                  type="button"
                  style={{ display: "flex" }}
                  onClick={() => handleRemoveClientIp(index)}
                >
                  <TrashBucketIcon width="16" height="16" />
                  <p style={{ marginLeft: "5px" }}>Delete</p>
                </button>
              </li>
            ))
          ) : (
            <li className="no-data">&nbsp;No Data</li>
          )}
        </ul>
        <br />
        <div className="mailing__content__security m-b-10">
          <p className="light m-b-10 mailing__content__security__header">
            BCC Mode
            <Tooltip
              text={
                "If enabled, mta will only listen the related SMTP port and mails will be dropped after analyzing the content. This is useful when you want to use your own MTA and just want to use our content filtering capabilities."
              }
              showFlowGrow={true}
            />
          </p>
          <div className="mailing__content__security__labels">
            <label className="align-items">
              <input
                className="mailing__content__security__input"
                type="radio"
                value="1"
                name="bcc_mode"
                checked={settings.bcc_mode === "1"}
                onChange={handleInputChange}
              />
              <p className="margin-left-12">Enabled</p>
            </label>
            <label className="align-items">
              <input
                className="mailing__content__security__input"
                type="radio"
                value="0"
                name="bcc_mode"
                checked={settings.bcc_mode === "0"}
                onChange={handleInputChange}
              />
              <p className="margin-left-12">Disabled</p>
            </label>
          </div>
        </div>
        <div className="products-body-form__line products-body-form__line--space">
          <Field
            label="RCPT Domain"
            width="220px"
            value={settings.newDomain?.domain}
            onChange={(e) =>
              setSettings((prev) => ({
                ...prev,
                newDomain: { ...prev.newDomain, domain: e.target.value },
              }))
            }
            parentClassName="p-r-20"
            placeholder="Domain (e.g., example.com)"
            name="newDomainDomain"
            disabled={settings.bcc_mode === "1"}
          />
          <Field
            label="Relay"
            width="220px"
            value={settings.newDomain?.relay}
            onChange={(e) =>
              setSettings((prev) => ({
                ...prev,
                newDomain: { ...prev.newDomain, relay: e.target.value },
              }))
            }
            parentClassName="p-r-20"
            placeholder="Relay (e.g., [192.168.1.142]:25)"
            name="newDomainRelay"
            disabled={settings.bcc_mode === "1"}
          />
          <button type="button" className="products-body-form__button" onClick={handleAddDomain} disabled={settings.bcc_mode === "1"}>
            +
          </button>
        </div>
        {settings.bcc_mode === "0" && (
          <ul className="rcpt-domains-list" style={{ gridColumn: "span 2" }}>
            {settings.rcptDomains?.length > 0 ? (
              settings.rcptDomains?.map((item, index) => (
                <li key={index} className="rcpt-domain-item">
                  {item.domain} - {item.relay}
                  <button
                    type="button"
                    style={{ display: "flex" }}
                    onClick={() => handleRemoveDomain(index)}
                  >
                    <TrashBucketIcon width="16" height="16" />
                    <p style={{ marginLeft: "5px" }}>Delete</p>
                  </button>
                </li>
              ))
            ) : (
              <li className="no-data">&nbsp;No Data</li>
            )}
          </ul>
        )}
        <br />
        <button type="button" className="save-button primary-button" onClick={handleSave}>
          Save Settings
        </button>
      </form>
    </>
  );
};

export default MailTransferForm;
